import { useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  Radio, MapPin, Users, Repeat, Plus, Trash2, Play, Info, UserPlus, ChevronRight, Gauge, Target, Lock, Download, Scale,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ROTATIONS, rotationMeta, addPlayer, addPlayers, removePlayer, startSession, SCORING_MODES } from "@/lib/openPlayStore";
import ImportPlayersModal from "@/components/ImportPlayersModal";
import PlayerNameSearch from "@/components/PlayerNameSearch";
import ClubsModal from "@/components/ClubsModal";
import { useAuth } from "@/context/AuthContext";
import { hasCapability } from "@/lib/tournament";

const SKILLS = [
  { v: 1, l: "1 · Beginner" }, { v: 2, l: "2 · Improver" }, { v: 3, l: "3 · Intermediate" },
  { v: 4, l: "4 · Advanced" }, { v: 5, l: "5 · Pro" },
];

export default function SessionSetup({ state, setState }) {
  const [eventName, setEventName] = useState(state.session?.eventName || "");
  const [location, setLocation] = useState(state.session?.location || "");
  const [mode, setMode] = useState(state.settings?.mode || "doubles");
  const [rotation, setRotation] = useState(state.settings?.rotation || "open_play");
  const [scoringMode, setScoringMode] = useState(state.settings?.scoringMode || "casual");
  const { user } = useAuth();
  const canAdvanced = hasCapability(user, "op_advanced_scoring");
  const effectiveMode = canAdvanced ? scoringMode : "casual";
  const [winningScore, setWinningScore] = useState(state.settings?.winningScoreThreshold || 11);
  const [gamesTarget, setGamesTarget] = useState(state.settings?.gamesTarget || 0);
  const [minGamesToRank, setMinGamesToRank] = useState(state.settings?.minGamesToRank ?? 5);
  const [name, setName] = useState("");
  const [skill, setSkill] = useState(3);
  const [balanced, setBalanced] = useState(state.settings?.balanced ?? true);
  const [importOpen, setImportOpen] = useState(false);
  const [clubsOpen, setClubsOpen] = useState(false);
  const inputRef = useRef(null);

  const meta = rotationMeta(rotation);
  const minPlayers = mode === "singles" ? 2 : 4;

  const add = (e) => {
    e.preventDefault();
    addByName(name, skill);
  };

  const addByName = (nm, sk) => {
    const { state: next, error } = addPlayer(state, nm, sk ?? skill);
    if (error) { toast.error(error); return; }
    setState(next);
    setName("");
    inputRef.current?.focus();
  };

  const handleImport = (names) => {
    const { state: next, added, skipped } = addPlayers(state, names, skill);
    setState(next);
    if (skipped.length) toast.message(`${skipped.length} duplicate${skipped.length === 1 ? "" : "s"} skipped.`);
    return added;
  };

  const start = () => {
    if (state.players.length < minPlayers) {
      toast.error(`Add at least ${minPlayers} players to start ${mode}.`);
      return;
    }
    setState(startSession(state, { eventName, location, mode, rotation, scoringMode: effectiveMode, winningScoreThreshold: winningScore, gamesTarget, minGamesToRank, balanced }));
    toast.success("Session started");
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 grain">
      <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <span className="text-xs tracking-[0.25em] uppercase font-bold text-primary-c flex items-center gap-2">
          <Radio className="w-3.5 h-3.5" /> New Session
        </span>
        <h1 className="font-display font-bold tracking-tight text-3xl sm:text-4xl mt-2">Set up your open play.</h1>
        <p className="mt-2 text-sm" style={{ color: "var(--text-2)" }}>Configure the event, pick a rotation and add your players before staging courts.</p>
      </motion.div>

      <div className="mt-8 rounded-2xl glow-border p-6 sm:p-8 space-y-6" style={{ background: "var(--surface)" }}>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-primary-c flex items-center gap-2 mb-2"><Radio className="w-3.5 h-3.5" /> Event name</label>
            <Input data-testid="setup-event-name" value={eventName} onChange={(e) => setEventName(e.target.value)}
              placeholder="Friday Night Open Play" className="bg-surface glow-border focus:ring-2 focus:ring-lime-400" />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-primary-c flex items-center gap-2 mb-2"><MapPin className="w-3.5 h-3.5" /> Location</label>
            <Input data-testid="setup-location" value={location} onChange={(e) => setLocation(e.target.value)}
              placeholder="Riverside Courts" className="bg-surface glow-border focus:ring-2 focus:ring-lime-400" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-primary-c flex items-center gap-2 mb-2"><Users className="w-3.5 h-3.5" /> Format</label>
            <div className="glass rounded-full p-1 flex" data-testid="setup-mode-toggle">
              {["doubles", "singles"].map((m) => (
                <button key={m} data-testid={`setup-mode-${m}`} onClick={() => setMode(m)}
                  className={`flex-1 px-4 py-2 rounded-full text-sm font-bold capitalize transition-colors ${mode === m ? "btn-glow" : ""}`}
                  style={mode === m ? {} : { color: "var(--text-2)" }}>{m}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase font-bold text-primary-c flex items-center gap-2 mb-2">
              <Repeat className="w-3.5 h-3.5" /> Rotation
              <Popover>
                <PopoverTrigger asChild>
                  <button data-testid="rotation-info" aria-label="How rotations work" className="ml-1 hover:scale-110 transition-transform">
                    <Info className="w-3.5 h-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="glass w-80" style={{ background: "var(--surface-glass)" }} data-testid="rotation-info-content">
                  <div className="font-display font-bold text-sm text-primary-c">{meta.name}</div>
                  <div className="text-xs italic mb-2" style={{ color: "var(--text-2)" }}>{meta.tagline}</div>
                  <p className="text-sm leading-relaxed" style={{ color: "var(--text)" }}>{meta.info}</p>
                </PopoverContent>
              </Popover>
            </label>
            <select data-testid="setup-rotation-select" value={rotation} onChange={(e) => setRotation(e.target.value)}
              className="w-full rounded-md bg-surface glow-border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }}>
              {ROTATIONS.map((r) => <option key={r.id} value={r.id}>{`${r.name} — ${r.tagline}`}</option>)}
            </select>
          </div>
        </div>

        {/* balanced game */}
        <div>
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-primary-c flex items-center gap-2 mb-2">
            <Scale className="w-3.5 h-3.5" /> Balanced Game
          </label>
          <button type="button" data-testid="setup-balanced-toggle" onClick={() => setBalanced((b) => !b)}
            className="w-full glass rounded-xl px-4 py-3 flex items-center justify-between text-left">
            <span className="text-sm pr-3" style={{ color: "var(--text-2)" }}>
              {balanced
                ? "ON — teams are matched by skill (strongest paired with weakest) for the fairest game. Repeat partners/opponents are avoided as a tie-break."
                : "OFF — the engine maximises unique partners & opponents first, only repeating when every combination has been used."}
            </span>
            <span className={`shrink-0 w-12 h-7 rounded-full p-0.5 transition-colors ${balanced ? "bg-primary-c" : ""}`}
              style={balanced ? {} : { background: "var(--border)" }}>
              <span className={`block w-6 h-6 rounded-full bg-white transition-transform ${balanced ? "translate-x-5" : ""}`} />
            </span>
          </button>
        </div>

        {/* scoring mode */}
        <div>
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-primary-c flex items-center gap-2 mb-2"><Gauge className="w-3.5 h-3.5" /> Scoring Mode</label>
          <div className="glass rounded-full p-1 flex" data-testid="setup-scoring-toggle">
            {["casual", "advanced"].map((m) => {
              const locked = m === "advanced" && !canAdvanced;
              return (
                <button key={m} data-testid={`setup-scoring-${m}`} onClick={() => !locked && setScoringMode(m)} disabled={locked}
                  title={locked ? "Advanced Scoring Mode isn't included in your plan" : undefined}
                  className={`flex-1 px-4 py-2 rounded-full text-sm font-bold transition-colors flex items-center justify-center gap-1.5 ${effectiveMode === m ? "btn-glow" : ""} ${locked ? "opacity-50 cursor-not-allowed" : ""}`}
                  style={effectiveMode === m ? {} : { color: "var(--text-2)" }}>
                  {locked && <Lock className="w-3 h-3" />}{SCORING_MODES[m].label}
                </button>
              );
            })}
          </div>
          <p className="text-xs mt-2" style={{ color: "var(--text-2)" }} data-testid="scoring-mode-subtext">
            {!canAdvanced ? "Advanced Scoring Mode isn't included in your current plan — upgrade to unlock win-rate, PPM & score-differential rankings." : SCORING_MODES[effectiveMode].subtext}
          </p>
          <div className="mt-3 flex items-center gap-3">
            <label className="text-xs flex items-center gap-1.5" style={{ color: "var(--text-2)" }}><Target className="w-3.5 h-3.5 text-primary-c" /> Winning score</label>
            <input data-testid="setup-winning-score" type="number" min="1" value={winningScore}
              onChange={(e) => setWinningScore(Number(e.target.value) || 1)}
              className="w-24 rounded-md bg-surface glow-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
            <span className="text-xs mono" style={{ color: "var(--text-2)" }}>points</span>
          </div>
        </div>

        {/* games target mode */}
        <div>
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-primary-c flex items-center gap-2 mb-2"><Target className="w-3.5 h-3.5" /> Games per player (target)</label>
          <div className="flex items-center gap-3">
            <input data-testid="setup-games-target" type="number" min="0" value={gamesTarget}
              onChange={(e) => setGamesTarget(Math.max(0, Number(e.target.value) || 0))}
              className="w-24 rounded-md bg-surface glow-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
            <span className="text-xs" style={{ color: "var(--text-2)" }}>
              {gamesTarget > 0
                ? `Every player is guaranteed ${gamesTarget} games — late arrivals are prioritised to catch up fairly.`
                : "0 = unlimited (classic rotation, no per-player target)."}
            </span>
          </div>
        </div>

        {/* minimum games to be ranked */}
        <div>
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-primary-c flex items-center gap-2 mb-2"><Target className="w-3.5 h-3.5" /> Minimum games to rank</label>
          <div className="flex items-center gap-3">
            <input data-testid="setup-min-games-rank" type="number" min="0" value={minGamesToRank}
              onChange={(e) => setMinGamesToRank(Math.max(0, Number(e.target.value) || 0))}
              className="w-24 rounded-md bg-surface glow-border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }} />
            <span className="text-xs" style={{ color: "var(--text-2)" }}>
              {minGamesToRank > 0
                ? `Players need ${minGamesToRank}+ games to enter the ranked standings — below that they're listed as provisional.`
                : "0 = everyone is ranked from their first game."}
            </span>
          </div>
        </div>

        {/* players */}
        <div>
          <label className="text-xs tracking-[0.2em] uppercase font-bold text-primary-c flex items-center gap-2 mb-2">
            <UserPlus className="w-3.5 h-3.5" /> Players <span style={{ color: "var(--text-2)" }}>({state.players.length})</span>
            <button type="button" data-testid="open-clubs" onClick={() => setClubsOpen(true)}
              className="ml-auto text-xs font-bold flex items-center gap-1.5 rounded-full px-3 py-1.5 glass hover:scale-[1.03] transition-transform" style={{ color: "var(--text)" }}>
              <Users className="w-3.5 h-3.5 text-primary-c" /> Clubs
            </button>
            <button type="button" data-testid="open-import-players" onClick={() => setImportOpen(true)}
              className="text-xs font-bold flex items-center gap-1.5 rounded-full px-3 py-1.5 glass hover:scale-[1.03] transition-transform" style={{ color: "var(--text)" }}>
              <Download className="w-3.5 h-3.5 text-primary-c" /> Import
            </button>
          </label>
          <form onSubmit={add} className="flex gap-3">
            <div className="flex-1">
              <PlayerNameSearch value={name} onChange={setName} testid="setup-player-search"
                inputRef={inputRef} placeholder="Search or add a player"
                excludeNames={state.players.map((p) => p.name)}
                onPick={(p) => { setSkill(p.skillLevel); addByName(p.name, p.skillLevel); }}
                onCreate={(nm) => addByName(nm, skill)} />
            </div>
            <select data-testid="setup-skill-select" value={skill} onChange={(e) => setSkill(Number(e.target.value))}
              className="rounded-md bg-surface glow-border px-3 text-sm outline-none focus:ring-2 focus:ring-lime-400" style={{ color: "var(--text)" }}>
              {SKILLS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
            <button data-testid="setup-add-player" type="submit" className="btn-glow rounded-md px-5 font-bold flex items-center gap-2 shrink-0">
              <Plus className="w-4 h-4" /> Add
            </button>
          </form>

          {state.players.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2" data-testid="setup-player-list">
              {state.players.map((p) => (
                <span key={p.id} className="flex items-center gap-2 rounded-full px-3 py-1.5 text-sm glass">
                  {p.name} <span className="mono text-xs text-primary-c">L{p.skillLevel}</span>
                  <button data-testid={`setup-remove-${p.id}`} onClick={() => setState(removePlayer(state, p.id))}
                    aria-label="Remove" className="hover:scale-110 transition-transform">
                    <Trash2 className="w-3 h-3" style={{ color: "var(--text-2)" }} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <button data-testid="start-session-button" onClick={start}
          className="btn-glow w-full rounded-full py-3.5 font-bold flex items-center justify-center gap-2">
          <Play className="w-5 h-5" /> Start Session <ChevronRight className="w-4 h-4" />
        </button>
        <p className="text-xs text-center" style={{ color: "var(--text-2)" }}>Need at least {minPlayers} players for {mode}.</p>
      </div>
      <ImportPlayersModal open={importOpen} onClose={() => setImportOpen(false)} onImport={handleImport} />
      <ClubsModal open={clubsOpen} onClose={() => setClubsOpen(false)}
        players={state.players.map((p) => ({ name: p.name, skillLevel: p.skillLevel }))} onLoad={handleImport} />
    </div>
  );
}
